import { create } from 'zustand';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, updateDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { verificarEAtualizarValidade } from '../utils/verificarValidade';

export const TODAS_ACOES = [
  'verDashboard',
  'verMeusTreinamentos',
  'verBiblioteca',
  'verCompetencias',
  'verPesquisas',
  'verColaboradores',
  'verRelatorios',
  'verConfiguracoes',
  'verPermissoes',
  'cadastrarColaborador',
  'editarColaborador',
  'inativarColaborador',
  'gerenciarCursos',
  'gerenciarMascaras',
  'verAuditoria',
  'verAgenda',
  'criarPesquisa',
  'exportarRelatorio',
  'editarCompetencias',
  'configurarNotificacoes',
  'configurarLinks',
];

export const MATRIZ_DEFAULT = {
  nivel1: {
    verDashboard: true,
    verMeusTreinamentos: true,
    verBiblioteca: true,
    verCompetencias: true,
    verPesquisas: true,
  },
  nivel2: {
    verDashboard: true,
    verMeusTreinamentos: true,
    verBiblioteca: true,
    verCompetencias: true,
    verPesquisas: true,
  },
  nivel3: {
    verDashboard: true,
    verMeusTreinamentos: true,
    verBiblioteca: true,
    verCompetencias: true,
    verPesquisas: true,
    verColaboradores: true,
    verRelatorios: true,
    verAgenda: true,
    criarPesquisa: true,
    exportarRelatorio: true,
    editarCompetencias: true,
  },
  nivel4: {
    verDashboard: true,
    verMeusTreinamentos: true,
    verBiblioteca: true,
    verCompetencias: true,
    verPesquisas: true,
    verColaboradores: true,
    verRelatorios: true,
    verAgenda: true,
    verConfiguracoes: true,
    cadastrarColaborador: true,
    editarColaborador: true,
    inativarColaborador: true,
    gerenciarCursos: true,
    gerenciarMascaras: true,
    verAuditoria: true,
    criarPesquisa: true,
    exportarRelatorio: true,
    editarCompetencias: true,
    configurarNotificacoes: true,
    configurarLinks: true,
  },
  nivel5: Object.fromEntries(
    [
      'verDashboard', 'verMeusTreinamentos', 'verBiblioteca', 'verCompetencias',
      'verPesquisas', 'verColaboradores', 'verRelatorios', 'verConfiguracoes',
      'verPermissoes', 'cadastrarColaborador', 'editarColaborador', 'inativarColaborador',
      'gerenciarCursos', 'criarPesquisa', 'exportarRelatorio', 'editarCompetencias',
      'configurarNotificacoes', 'configurarLinks',
    ].map(a => [a, true])
  ),
};

const useAuthStore = create((set) => ({
  usuario: null,
  nivel: 0,
  perfil: null,
  permissoes: {},
  carregando: true,

  inicializar: () => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          if (user.email === 'andre.paiva@jatear.com') {
            try {
              const tokenResult = await user.getIdTokenResult();
              if (!tokenResult.claims.admin) {
                await httpsCallable(functions, 'promoverAdmin')();
                await user.getIdToken(true); // força refresh do token para ativar custom claim
              }
            } catch (err) {
              console.error('promoverAdmin:', err);
            }
          }

          const matrizSnap = await getDoc(doc(db, 'parametros', 'permissoes'));
          const matrizFirestore = matrizSnap.exists() ? matrizSnap.data() : null;
          const matriz = matrizFirestore || MATRIZ_DEFAULT;

          verificarEAtualizarValidade(user.uid).catch(() => {});
          const agora = new Date().toISOString();
          updateDoc(doc(db, 'usuarios', user.uid), { ultimoAcesso: agora }).catch(() => {});
          // Atualiza ultimoAcesso também no documento de colaborador — fire-and-forget
          getDocs(query(collection(db, 'colaboradores'), where('uid', '==', user.uid))).then(snap => {
            snap.docs.forEach(d => updateDoc(d.ref, { ultimoAcesso: agora }).catch(() => {}));
          }).catch(() => {});

          // onSnapshot substitui getDoc inicial: dispara imediatamente com dados atuais (cache local)
          onSnapshot(doc(db, 'usuarios', user.uid), (snap) => {
            if (!snap.exists()) {
              set({ usuario: user, nivel: 1, perfil: {}, permissoes: MATRIZ_DEFAULT.nivel1, carregando: false });
              return;
            }
            let dados = snap.data();
            if (user.email === 'andre.paiva@jatear.com') {
              dados = { ...dados, uid: user.uid, email: user.email, nivel: 5 };
            }
            const nivel = Number(dados.nivel) || 1;
            const permissoes = matriz[`nivel${nivel}`] || MATRIZ_DEFAULT[`nivel${nivel}`] || {};
            set({ usuario: user, nivel, perfil: dados, permissoes, carregando: false });
          }, () => {});
        } catch (e) {
          console.error('authStore:', e);
          set({
            usuario: user,
            nivel: 1,
            perfil: {},
            permissoes: MATRIZ_DEFAULT.nivel1,
            carregando: false,
          });
        }
      } else {
        set({ usuario: null, nivel: 0, perfil: null, permissoes: {}, carregando: false });
      }
    });
    return unsub;
  },

  logout: async () => {
    await auth.signOut();
    set({ usuario: null, nivel: 0, perfil: null, permissoes: {} });
  },

  recarregarPermissoes: async (nivel) => {
    try {
      const matrizSnap = await getDoc(doc(db, 'parametros', 'permissoes'));
      const matriz = matrizSnap.exists() ? matrizSnap.data() : MATRIZ_DEFAULT;
      const permissoes =
        matriz[`nivel${nivel}`] || MATRIZ_DEFAULT[`nivel${nivel}`] || {};
      set({ permissoes });
    } catch {}
  },
}));

export default useAuthStore;
